import React, { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, FileText, Sparkles, Cog } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';

/**
 * AIJobProgress
 * Polls /api/jobs/{jobId} every 1.5 s until completed/failed and renders a
 * live progress card with substep label + percentage bar.
 *
 * Props:
 *   jobId — the job to track
 *   onComplete(result) — called when status === 'completed' with the result payload
 *   onFail(error) — called when status === 'failed'
 *   onCancel() — optional close button handler (also fires cancel API)
 *   title — optional title override
 */
export default function AIJobProgress({ jobId, onComplete, onFail, onCancel, title }) {
  const [job, setJob] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!jobId) return;
    stoppedRef.current = false;
    let timer;
    const poll = async () => {
      try {
        const j = await apiGet(`/jobs/${jobId}`);
        setJob(j);
        if (j.status === 'completed') {
          stoppedRef.current = true;
          onComplete && onComplete(j.result || {});
          return;
        }
        if (j.status === 'failed') {
          stoppedRef.current = true;
          onFail && onFail(j.error || 'AI job failed');
          return;
        }
      } catch (e) {
        // Network glitch — keep trying; only abort after 5 consecutive failures
      }
      if (!stoppedRef.current) timer = setTimeout(poll, 1500);
    };
    poll();
    const elTimer = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => { stoppedRef.current = true; clearTimeout(timer); clearInterval(elTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const cancel = async () => {
    try { await apiPost(`/jobs/${jobId}/cancel`); } catch {}
    onCancel && onCancel();
  };

  const stepIcon = (step) => {
    if (step === 'reading_docs') return <FileText size={14} className="text-sky-600" />;
    if (step === 'calling_claude') return <Sparkles size={14} className="text-violet-600" />;
    if (step === 'parsing') return <Cog size={14} className="text-amber-600" />;
    if (step === 'completed') return <CheckCircle2 size={14} className="text-emerald-600" />;
    return <Loader2 size={14} className="text-slate-500 animate-spin" />;
  };

  const status = job?.status || 'queued';
  const pct = job?.progress_pct ?? 5;
  const label = job?.step_label || 'Queued…';

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-3" data-testid="ai-job-progress">
      <div className="flex items-center justify-between">
        <div className="kpi-overline flex items-center gap-1.5">
          {stepIcon(job?.step)}
          {title || 'AI Job in Progress'}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono text-slate-400">
          <span>{elapsed}s elapsed</span>
          <span>{status}</span>
          <button onClick={cancel} className="text-rose-600 hover:underline" data-testid="ai-job-cancel">Cancel</button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${status === 'failed' ? 'bg-rose-500' : status === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#3b4872] to-violet-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Substep label */}
      <div className="text-sm text-slate-700 flex items-center gap-2" data-testid="ai-job-step-label">
        {status !== 'completed' && status !== 'failed' && <Loader2 size={12} className="animate-spin shrink-0" />}
        <span className="flex-1">{label}</span>
        <span className="text-xs font-mono text-slate-400 tabular-nums">{pct}%</span>
      </div>

      {/* Step timeline */}
      <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-2">
        <Step done={['reading_docs','calling_claude','parsing','completed'].includes(job?.step)} active={job?.step === 'reading_docs'} label="Reading documents" />
        <span className="text-slate-300">→</span>
        <Step done={['calling_claude','parsing','completed'].includes(job?.step)} active={job?.step === 'calling_claude'} label="Calling Ellora AI" />
        <span className="text-slate-300">→</span>
        <Step done={['parsing','completed'].includes(job?.step)} active={job?.step === 'parsing'} label="Parsing" />
        <span className="text-slate-300">→</span>
        <Step done={job?.step === 'completed'} active={false} label="Done" />
      </div>

      {status === 'failed' && (
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-2.5 text-xs text-rose-800 flex items-start gap-2" data-testid="ai-job-error">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <div className="flex-1">{job?.error || 'Job failed'}</div>
        </div>
      )}
    </div>
  );
}

function Step({ done, active, label }) {
  const cls = done ? 'text-emerald-700 font-display font-bold' :
              active ? 'text-[#3b4872] font-display font-bold' : 'text-slate-400';
  return <span className={cls}>{done ? '✓ ' : active ? '● ' : '○ '}{label}</span>;
}
